import type {
  ConsentCategoryConfiguration,
  ConsentClient,
  ConsentClientSnapshot,
  ConsentConfiguration,
  ConsentPreferenceInput,
  ConsentStorageKind,
  ConsentVendorConfiguration,
} from '@goksima/consent-core';

import { STYLES } from './styles.js';
import { DEFAULT_TEXTS } from './texts.js';
import type { ConsentUiLanguage, ConsentUiTexts } from './texts.js';

export type ConsentUiClient = Pick<
  ConsentClient,
  'getSnapshot' | 'on' | 'acceptAll' | 'rejectAll' | 'setPreferences' | 'retry'
>;

export interface ConsentUiTheme {
  readonly font?: string;
  readonly background?: string;
  readonly text?: string;
  readonly muted?: string;
  readonly border?: string;
  readonly buttonBackground?: string;
  readonly buttonText?: string;
  readonly buttonBorder?: string;
  readonly accent?: string;
  readonly accentText?: string;
  readonly radius?: string;
  readonly buttonRadius?: string;
  readonly shadow?: string;
  readonly backdrop?: string;
  readonly zIndex?: string | number;
}

export interface ConsentUiLogo {
  readonly src: string;
  readonly alt: string;
}

export type ConsentUiPosition = 'bottom' | 'bottom-left' | 'bottom-right';

export interface ConsentUiOptions {
  readonly client: ConsentUiClient;
  readonly language?: ConsentUiLanguage;
  readonly texts?: Partial<ConsentUiTexts>;
  readonly logo?: ConsentUiLogo;
  readonly policyUrl?: string;
  readonly theme?: ConsentUiTheme;
  readonly position?: ConsentUiPosition;
  readonly container?: Element;
  readonly document?: Document;
}

export interface ConsentUi {
  open(): void;
  close(): void;
  destroy(): void;
}

export type ConsentUiErrorCode =
  | 'already_mounted'
  | 'document_unavailable'
  | 'invalid_options';

export class ConsentUiError extends Error {
  readonly code: ConsentUiErrorCode;

  constructor(code: ConsentUiErrorCode, message: string) {
    super(message);
    this.name = 'ConsentUiError';
    this.code = code;
  }
}

/** Any element carrying this attribute reopens the preferences dialog. */
export const OPEN_ATTRIBUTE = 'data-consent-open';

/** Elements carrying this attribute are filled with the site's service table. */
export const COOKIE_TABLE_ATTRIBUTE = 'data-consent-cookie-table';

const THEME_PROPERTIES: Readonly<Record<keyof ConsentUiTheme, string>> = {
  font: '--consent-font',
  background: '--consent-bg',
  text: '--consent-text',
  muted: '--consent-muted',
  border: '--consent-border',
  buttonBackground: '--consent-button-bg',
  buttonText: '--consent-button-text',
  buttonBorder: '--consent-button-border',
  accent: '--consent-accent',
  accentText: '--consent-accent-text',
  radius: '--consent-radius',
  buttonRadius: '--consent-button-radius',
  shadow: '--consent-shadow',
  backdrop: '--consent-backdrop',
  zIndex: '--consent-z',
};

const POSITIONS: readonly ConsentUiPosition[] = [
  'bottom',
  'bottom-left',
  'bottom-right',
];

const mountedDocuments = new WeakSet<Document>();

function hasOwn(object: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function invalid(message: string): ConsentUiError {
  return new ConsentUiError('invalid_options', message);
}

function resolveLanguage(
  doc: Document,
  language: ConsentUiLanguage | undefined,
): ConsentUiLanguage {
  if (language !== undefined) {
    if (!hasOwn(DEFAULT_TEXTS, language)) {
      throw invalid('Language must be one of the bundled text sets.');
    }
    return language;
  }
  const pageLanguage = doc.documentElement.lang.toLowerCase().split('-')[0];
  return pageLanguage === 'en' ? 'en' : 'tr';
}

function resolveTexts(
  language: ConsentUiLanguage,
  overrides: Partial<ConsentUiTexts> | undefined,
): ConsentUiTexts {
  const base = DEFAULT_TEXTS[language];
  if (overrides === undefined) return base;

  const texts: Record<string, string> = { ...base };
  for (const key of Object.keys(base)) {
    if (!hasOwn(overrides, key)) continue;
    const value: unknown = overrides[key as keyof ConsentUiTexts];
    if (typeof value !== 'string' || value.trim() === '') {
      throw invalid(`Text "${key}" must be a non-empty string.`);
    }
    texts[key] = value;
  }
  return texts as unknown as ConsentUiTexts;
}

function resolveUrl(doc: Document, value: unknown, field: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw invalid(`${field} must be a non-empty URL.`);
  }
  let url: URL;
  try {
    url = new URL(value, doc.baseURI);
  } catch {
    throw invalid(`${field} must be a valid URL.`);
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw invalid(`${field} must use http or https.`);
  }
  if (url.username !== '' || url.password !== '') {
    throw invalid(`${field} must not contain credentials.`);
  }
  return url.href;
}

function isClient(value: unknown): value is ConsentUiClient {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    ['getSnapshot', 'on', 'acceptAll', 'rejectAll', 'setPreferences', 'retry'] as const
  ).every((method) => typeof candidate[method] === 'function');
}

function configOf(snapshot: ConsentClientSnapshot): ConsentConfiguration | null {
  return snapshot.status === 'ready' || snapshot.status === 'required'
    ? snapshot.config
    : null;
}

function isLocked(category: ConsentCategoryConfiguration): boolean {
  return category.required || category.key === 'necessary';
}

function vendorsIn(
  config: ConsentConfiguration,
  category: ConsentCategoryConfiguration,
): readonly ConsentVendorConfiguration[] {
  return config.vendors.filter((vendor) => vendor.category === category.key);
}

// With nothing optional to allow, asking for a choice would be theatre:
// the visitor is informed and the decision is stored as necessary-only.
function isNoticeOnly(config: ConsentConfiguration): boolean {
  return config.vendors.every((vendor) => vendor.category === 'necessary');
}

export function mountConsentUi(options: ConsentUiOptions): ConsentUi {
  if (typeof options !== 'object' || options === null) {
    throw invalid('Options must be an object.');
  }
  const { client } = options;
  if (!isClient(client)) {
    throw invalid('A consent client with the core API is required.');
  }

  const doc = options.document ?? globalThis.document;
  if (!doc) {
    throw new ConsentUiError(
      'document_unavailable',
      'Consent UI needs a DOM document.',
    );
  }
  if (mountedDocuments.has(doc)) {
    throw new ConsentUiError(
      'already_mounted',
      'Consent UI is already mounted in this document.',
    );
  }

  const position = options.position ?? 'bottom';
  if (!POSITIONS.includes(position)) {
    throw invalid('Position must be bottom, bottom-left or bottom-right.');
  }
  const language = resolveLanguage(doc, options.language);
  const texts = resolveTexts(language, options.texts);
  const policyUrl =
    options.policyUrl === undefined
      ? null
      : resolveUrl(doc, options.policyUrl, 'policyUrl');
  let logo: { readonly src: string; readonly alt: string } | null = null;
  if (options.logo !== undefined) {
    if (typeof options.logo.alt !== 'string') {
      throw invalid('logo.alt must be a string.');
    }
    logo = { src: resolveUrl(doc, options.logo.src, 'logo.src'), alt: options.logo.alt };
  }
  const container = options.container ?? doc.body;
  if (!container) {
    throw new ConsentUiError(
      'document_unavailable',
      'Consent UI needs a container element or a document body.',
    );
  }

  function el<Tag extends keyof HTMLElementTagNameMap>(
    tag: Tag,
    attributes: Readonly<Record<string, string>> = {},
    ...children: readonly (Node | string)[]
  ): HTMLElementTagNameMap[Tag] {
    const element = doc.createElement(tag);
    for (const [name, value] of Object.entries(attributes)) {
      element.setAttribute(name, value);
    }
    element.append(...children);
    return element;
  }

  function button(label: string, action: string): HTMLButtonElement {
    return el(
      'button',
      { type: 'button', class: 'button', part: 'button', 'data-action': action },
      label,
    );
  }

  function logoImage(): HTMLImageElement | null {
    return logo
      ? el('img', { class: 'logo', part: 'logo', src: logo.src, alt: logo.alt })
      : null;
  }

  function bodyWithPolicy(text: string, id: string): [HTMLParagraphElement, Text] {
    const content = doc.createTextNode(text);
    const paragraph = el('p', { class: 'body', id }, content);
    if (policyUrl) {
      paragraph.append(' ', el('a', { href: policyUrl, part: 'link' }, texts.policyLink));
    }
    return [paragraph, content];
  }

  function kindLabel(kind: ConsentStorageKind): string {
    return kind === 'cookie'
      ? texts.kindCookie
      : kind === 'localStorage'
        ? texts.kindLocalStorage
        : texts.kindSessionStorage;
  }

  function vendorPolicyLink(vendor: ConsentVendorConfiguration): HTMLAnchorElement | null {
    return vendor.policyUrl
      ? el('a', { href: vendor.policyUrl, rel: 'noopener noreferrer', part: 'link' }, texts.vendorPolicy)
      : null;
  }

  function table(
    headings: readonly string[],
    rows: readonly (readonly (Node | string)[])[],
  ): HTMLTableElement {
    return el(
      'table',
      {},
      el('thead', {}, el('tr', {}, ...headings.map((heading) => el('th', { scope: 'col' }, heading)))),
      el('tbody', {}, ...rows.map((cells) => el('tr', {}, ...cells.map((cell) => el('td', {}, cell))))),
    );
  }

  function vendorDetails(vendors: readonly ConsentVendorConfiguration[]): HTMLDetailsElement {
    return el(
      'details',
      { class: 'services', part: 'services' },
      el('summary', {}, `${texts.services} (${vendors.length})`),
      el(
        'ul',
        { class: 'vendors' },
        ...vendors.map((vendor) => {
          const link = vendorPolicyLink(vendor);
          return el(
            'li',
            { class: 'vendor' },
            el('p', {}, el('strong', {}, vendor.name), ` · ${vendor.provider}`),
            el('p', {}, vendor.purpose),
            el('p', { class: 'meta' }, `${texts.retention}: ${vendor.retention}`),
            ...(vendor.cookies.length > 0
              ? [
                  table(
                    [texts.storageName, texts.storageKind, texts.duration],
                    vendor.cookies.map((cookie) => [
                      el('code', {}, cookie.name),
                      kindLabel(cookie.kind),
                      cookie.duration,
                    ]),
                  ),
                ]
              : []),
            ...(link ? [link] : []),
          );
        }),
      ),
    );
  }

  // The policy page gets a plain table in the light DOM so it follows the
  // site's own typography.
  function cookieTableContent(config: ConsentConfiguration): Node[] {
    if (config.vendors.length === 0) return [el('p', {}, texts.noServices)];
    return config.categories.flatMap((category) => {
      const vendors = vendorsIn(config, category);
      if (vendors.length === 0) return [];
      const rows = vendors.flatMap((vendor) => {
        const row = (name: Node | string, kind: string, purpose: string, duration: string) => {
          const link = vendorPolicyLink(vendor);
          const provider = el('span', {}, vendor.provider, ...(link ? [el('br'), link] : []));
          return [vendor.name, provider, name, kind, purpose, duration];
        };
        return vendor.cookies.length === 0
          ? [row('—', '—', vendor.purpose, vendor.retention)]
          : vendor.cookies.map((cookie) =>
              row(
                el('code', {}, cookie.name),
                kindLabel(cookie.kind),
                cookie.purpose ?? vendor.purpose,
                cookie.duration,
              ),
            );
      });
      const result = table(
        [texts.service, texts.provider, texts.storageName, texts.storageKind, texts.purpose, texts.duration],
        rows,
      );
      result.className = 'consent-cookie-table';
      result.prepend(el('caption', {}, category.label));
      // Six columns do not fit a phone; the table scrolls, not the page.
      const scroller = el('div', { class: 'consent-cookie-table-scroll' }, result);
      scroller.style.overflowX = 'auto';
      return [scroller];
    });
  }

  function renderCookieTables(config: ConsentConfiguration): void {
    for (const target of doc.querySelectorAll(`[${COOKIE_TABLE_ATTRIBUTE}]`)) {
      target.replaceChildren(...cookieTableContent(config));
    }
  }

  const host = el('div', { 'data-consent-ui-root': '', 'data-position': position, lang: language });
  for (const [key, property] of Object.entries(THEME_PROPERTIES)) {
    const value: unknown = options.theme?.[key as keyof ConsentUiTheme];
    if (value === undefined) continue;
    if (typeof value !== 'string' && typeof value !== 'number') {
      throw invalid(`theme.${key} must be a string or number.`);
    }
    host.style.setProperty(property, String(value));
  }
  const shadow = host.attachShadow({ mode: 'open' });

  // Elements that belong to one of the two modes: a real choice, or a notice
  // for sites that use nothing optional.
  const choiceOnly: HTMLElement[] = [];
  const noticeOnly: HTMLElement[] = [];
  const choice = (...elements: HTMLElement[]) => (choiceOnly.push(...elements), elements);
  const notice = (...elements: HTMLElement[]) => {
    for (const element of elements) element.setAttribute('data-notice', '');
    noticeOnly.push(...elements);
    return elements;
  };

  // First layer: three choices of equal weight.
  const bannerLogo = logoImage();
  const [bannerBody, bannerBodyText] = bodyWithPolicy(texts.bannerBody, 'consent-banner-body');
  const bannerStatus = el('p', { class: 'status', role: 'status', 'aria-live': 'polite' });
  const banner = el(
    'section',
    {
      class: 'banner',
      part: 'banner',
      'aria-labelledby': 'consent-banner-title',
      'aria-describedby': 'consent-banner-body',
      hidden: '',
    },
    el(
      'div',
      { class: 'banner-text' },
      ...(bannerLogo ? [bannerLogo] : []),
      el('p', { class: 'title', id: 'consent-banner-title' }, texts.bannerTitle),
      bannerBody,
      bannerStatus,
    ),
    el(
      'div',
      { class: 'actions' },
      ...choice(
        button(texts.rejectAll, 'reject'),
        button(texts.settings, 'settings'),
        button(texts.acceptAll, 'accept'),
      ),
      ...notice(button(texts.details, 'settings'), button(texts.confirm, 'reject')),
    ),
  );

  // Second layer: per-category choices.
  const closeButton = el(
    'button',
    { type: 'button', class: 'close', part: 'close', 'aria-label': texts.close, 'data-action': 'close' },
    '×',
  );
  const dialogLogo = logoImage();
  const loading = el('p', { class: 'status' }, texts.loading);
  const retryButton = button(texts.retry, 'retry');
  const unavailable = el(
    'div',
    { class: 'unavailable', hidden: '' },
    el('p', { class: 'note' }, texts.unavailable),
    el('div', { class: 'actions' }, retryButton),
  );
  const categoryList = el('ul', { class: 'categories', hidden: '' });
  const withdrawalNote = el('p', { class: 'note withdrawal', hidden: '' }, texts.withdrawalNote);
  const [dialogBody, dialogBodyText] = bodyWithPolicy(texts.dialogBody, 'consent-dialog-body');
  const dialogStatus = el('p', { class: 'status', role: 'status', 'aria-live': 'polite' });
  const footer = el(
    'div',
    { class: 'footer', hidden: '' },
    dialogStatus,
    el(
      'div',
      { class: 'actions' },
      ...choice(
        button(texts.dialogRejectAll, 'reject'),
        button(texts.dialogAcceptAll, 'accept'),
        button(texts.save, 'save'),
      ),
      ...notice(button(texts.confirm, 'reject')),
    ),
  );
  const dialog = el(
    'dialog',
    {
      class: 'dialog',
      part: 'dialog',
      'aria-labelledby': 'consent-dialog-title',
      'aria-describedby': 'consent-dialog-body',
    },
    el(
      'div',
      { class: 'panel' },
      el(
        'div',
        { class: 'head' },
        el(
          'div',
          { class: 'head-text' },
          ...(dialogLogo ? [dialogLogo] : []),
          el('h2', { id: 'consent-dialog-title' }, texts.dialogTitle),
        ),
        closeButton,
      ),
      dialogBody,
      loading,
      unavailable,
      categoryList,
      withdrawalNote,
      footer,
    ),
  );

  shadow.append(el('style', {}, STYLES), banner, dialog);

  let renderedConfig: ConsentConfiguration | null = null;
  let noticeMode = false;
  let toggles = new Map<string, HTMLInputElement>();
  let busy = false;
  let returnFocus: HTMLElement | null = null;

  function renderCategories(config: ConsentConfiguration): void {
    toggles = new Map();
    noticeMode = isNoticeOnly(config);
    bannerBodyText.data = noticeMode ? texts.noticeBody : texts.bannerBody;
    dialogBodyText.data = noticeMode ? texts.noticeBody : texts.dialogBody;
    for (const element of choiceOnly) element.hidden = noticeMode;
    for (const element of noticeOnly) element.hidden = !noticeMode;

    // In notice mode every optional category is empty and the lede says so.
    const shown = noticeMode ? config.categories.filter(isLocked) : config.categories;
    categoryList.replaceChildren(
      ...shown.map((category) => {
        const titleId = `consent-category-${category.key}`;
        const descriptionId = `${titleId}-description`;
        const locked = isLocked(category);
        const vendors = vendorsIn(config, category);
        // An optional category with no service has nothing to switch on.
        const switchable = locked || vendors.length > 0;
        const input = el('input', {
          type: 'checkbox',
          role: 'switch',
          'aria-labelledby': titleId,
          ...(category.description ? { 'aria-describedby': descriptionId } : {}),
        });
        if (locked) {
          input.checked = true;
          input.disabled = true;
        } else if (switchable) {
          toggles.set(category.key, input);
        }
        return el(
          'li',
          { class: 'category', part: 'category' },
          el(
            'div',
            { class: 'category-head' },
            el('h3', { id: titleId }, category.label),
            ...(switchable
              ? [
                  el(
                    'span',
                    { class: 'switch', part: 'switch' },
                    ...(locked ? [el('span', { class: 'locked' }, texts.alwaysActive)] : []),
                    input,
                    el('span', { class: 'track', 'aria-hidden': 'true' }),
                  ),
                ]
              : []),
          ),
          ...(category.description
            ? [el('p', { id: descriptionId }, category.description)]
            : []),
          ...(vendors.length > 0
            ? [vendorDetails(vendors)]
            : locked
              ? []
              : [el('p', { class: 'empty' }, texts.emptyCategory)]),
        );
      }),
    );
    renderCookieTables(config);
    renderedConfig = config;
  }

  function syncToggles(): void {
    const snapshot = client.getSnapshot();
    const choices: Readonly<Record<string, boolean>> | null =
      snapshot.status === 'ready' ? snapshot.state.categories : null;
    for (const [key, input] of toggles) input.checked = choices?.[key] ?? false;
  }

  function render(snapshot: ConsentClientSnapshot): void {
    const config = configOf(snapshot);
    if (config && config !== renderedConfig) {
      renderCategories(config);
      if (!dialog.open) syncToggles();
    }

    banner.hidden = snapshot.status !== 'required';
    loading.hidden = snapshot.status !== 'idle' && snapshot.status !== 'initializing';
    unavailable.hidden = snapshot.status !== 'blocked';
    retryButton.hidden = !(snapshot.status === 'blocked' && snapshot.retryable);
    categoryList.hidden = config === null;
    footer.hidden = config === null;
    withdrawalNote.hidden = snapshot.status !== 'ready' || noticeMode;
  }

  function setBusy(value: boolean): void {
    busy = value;
    for (const control of shadow.querySelectorAll<HTMLButtonElement>('button[data-action]')) {
      if (control !== closeButton) control.disabled = value;
    }
    host.setAttribute('aria-busy', String(value));
  }

  function focusedElement(): HTMLElement | null {
    const active = doc.activeElement === host ? shadow.activeElement : doc.activeElement;
    return active && typeof (active as HTMLElement).focus === 'function'
      ? (active as HTMLElement)
      : null;
  }

  function open(): void {
    if (dialog.open) return;
    returnFocus = focusedElement();
    dialogStatus.textContent = '';
    syncToggles();
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    closeButton.focus();
  }

  function close(): void {
    if (!dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      restoreFocus();
    }
  }

  function restoreFocus(): void {
    const target = returnFocus;
    returnFocus = null;
    // A control inside a banner that has just been hidden cannot take focus.
    if (!target || !target.isConnected || (banner.hidden && banner.contains(target))) {
      return;
    }
    target.focus();
  }

  async function decide(
    action: () => Promise<unknown>,
    status: HTMLElement,
  ): Promise<void> {
    if (busy) return;
    setBusy(true);
    bannerStatus.textContent = '';
    dialogStatus.textContent = '';
    try {
      await action();
      close();
    } catch {
      status.textContent = texts.saveError;
    } finally {
      setBusy(false);
    }
  }

  // A category without a switch is saved as off, not left as it was.
  function savedPreferences(): ConsentPreferenceInput {
    const preferences: Record<string, boolean> = {};
    for (const category of renderedConfig?.categories ?? []) {
      if (isLocked(category)) continue;
      preferences[category.key] = toggles.get(category.key)?.checked ?? false;
    }
    return preferences;
  }

  async function retry(): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await client.retry();
    } catch {
      // The snapshot keeps reporting the blocked state; nothing else to show.
    } finally {
      setBusy(false);
    }
  }

  function onShadowClick(event: Event): void {
    const target = event.target as Element | null;
    const control = target?.closest?.('button[data-action]');
    if (!control || control.hasAttribute('disabled')) return;
    const status = banner.contains(control) ? bannerStatus : dialogStatus;

    switch (control.getAttribute('data-action')) {
      case 'accept':
        void decide(() => client.acceptAll(), status);
        break;
      case 'reject':
        void decide(() => client.rejectAll(), status);
        break;
      case 'save':
        void decide(() => client.setPreferences(savedPreferences()), status);
        break;
      case 'settings':
        open();
        break;
      case 'close':
        close();
        break;
      case 'retry':
        void retry();
        break;
    }
  }

  function onDocumentClick(event: Event): void {
    const target = event.target as Element | null;
    if (!target?.closest?.(`[${OPEN_ATTRIBUTE}]`)) return;
    event.preventDefault();
    open();
  }

  shadow.addEventListener('click', onShadowClick);
  dialog.addEventListener('close', restoreFocus);
  doc.addEventListener('click', onDocumentClick);
  const unsubscribe = client.on('status-changed', ({ current }) => render(current));

  render(client.getSnapshot());
  container.append(host);
  mountedDocuments.add(doc);

  return {
    open,
    close,
    destroy(): void {
      unsubscribe();
      doc.removeEventListener('click', onDocumentClick);
      close();
      host.remove();
      mountedDocuments.delete(doc);
    },
  };
}
